import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Search } from 'lucide-react';
import { useLearning } from '@/hooks/useLearning';
import { EnhancedLearningPathCard } from './EnhancedLearningPathCard';
import { CurrentPathCard } from './CurrentPathCard';
import { ViewToggle } from './ViewToggle';
import { UserProfile } from '@/types/career';
import { Skeleton } from '@/components/ui/skeleton';

interface EnhancedLearningPathsProps {
  currentStage: string;
  onPathSelect: (pathId: string) => void;
  selectedPathId: string | null;
  userProfile: any; // Replace with proper type
}

export function EnhancedLearningPaths({
  currentStage,
  onPathSelect,
  selectedPathId,
  userProfile
}: EnhancedLearningPathsProps) {
  const { currentPath, allPaths, startPath } = useLearning();
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header with Search and Filters */}
      <div className="flex items-center justify-between">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Search learning paths..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
        
        <div className="flex items-center gap-4">
          <Select
            value={filter}
            onValueChange={setFilter}
            options={[
              { label: 'All Paths', value: 'all' },
              { label: 'In Progress', value: 'progress' },
              { label: 'Not Started', value: 'new' },
              { label: 'Completed', value: 'completed' }
            ]}
          />
          
          <ViewToggle
            view={view}
            onChange={setView}
          />
        </div>
      </div>

      {/* Current Path Highlight */}
      {currentPath && (
        <CurrentPathCard path={currentPath} />
      )}

      {/* Paths Grid/List */}
      <motion.div 
        className={view === 'grid' ? 'grid grid-cols-3 gap-6' : 'space-y-4'}
        layout
      >
        <AnimatePresence>
          {allPaths.map(path => (
            <motion.div
              key={path.id}
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <EnhancedLearningPathCard
                path={path}
                onStart={startPath}
                profile={userProfile}
                isSelected={selectedPathId === path.id}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
} 